import React, { useEffect, useId, useRef } from "react";
import { BaseWalletIcon } from "./BaseWalletIcon";

/** Keep children mounted when closed: dismissing a dialog must not cancel proving. */
export function BaseWalletDialog({
  open,
  title,
  onClose,
  onBack,
  wide,
  settingsHome,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onBack?: () => void;
  wide?: boolean;
  settingsHome?: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !open) return;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
        previousFocus.focus();
    };
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`wallet-dialog${wide ? " wallet-dialog-wide" : ""}${settingsHome ? " wallet-dialog-settings" : ""}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) {
          const rect = ref.current!.getBoundingClientRect();
          if (
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom
          )
            onClose();
        }
      }}
    >
      <header className="wallet-dialog-header">
        {onBack && (
          <button
            type="button"
            className="wallet-icon-button"
            onClick={onBack}
            aria-label="Go back"
          >
            <BaseWalletIcon name="back" />
          </button>
        )}
        <h2 id={titleId}>{title}</h2>
        <button
          type="button"
          className="wallet-icon-button"
          onClick={onClose}
          aria-label="Close dialog"
        >
          <BaseWalletIcon name="close" />
        </button>
      </header>
      {children}
    </dialog>
  );
}
